import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { ControlContainer, FormArray, FormGroup } from '@angular/forms';
import { catchError, debounceTime, takeUntil, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { YaGeocoderService } from 'angular8-yandex-maps';
import { AlertService } from '@src/core/services';
import { BaseComponent } from '@src/app/modules/base';

import { FormProductAddressType, ValidationStatusType } from './types';

@Component({
  selector: 'form-field',
  templateUrl: './geocoder-form-field.component.html',
  styleUrls: ['./geocoder-form-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true }),
    },
  ],
})
export class GeocoderFormFieldComponent extends BaseComponent {
  private readonly alertService = inject(AlertService);
  private readonly yaGeocoderService = inject(YaGeocoderService);
  private readonly translateService = inject(TranslateService);
  private readonly mapObjectsContainer = inject(ControlContainer);

  @Input({ required: true }) controlKey = 0;
  @Output() deleteField = new EventEmitter<void>();

  validationStatus: ValidationStatusType = 'invalid';

  get mapObjectsArray() {
    return this.mapObjectsContainer.control as FormArray<FormGroup<FormProductAddressType>>;
  }

  get formGroup() {
    return this.mapObjectsArray.at(this.controlKey);
  }

  get addressControl() {
    return this.formGroup.controls.address;
  }

  get latitudeControl() {
    return this.formGroup.controls.latitude;
  }

  get longitudeControl() {
    return this.formGroup.controls.longitude;
  }

  get statusHint(): string {
    switch (this.validationStatus) {
      case 'valid':
        return this.translateService.instant('components.geocoder.labels.hintValid');

      case 'invalid':
        return this.translateService.instant('components.geocoder.labels.hintInvalid');

      default:
        return '';
    }
  }

  get statusIcon() {
    switch (this.validationStatus) {
      case 'valid':
        return '@tui.check';

      case 'invalid':
        return '@tui.circle-alert';

      default:
        return '';
    }
  }

  ngOnInit(): void {
    if (this.addressControl.value) {
      this.validateAddress();
    }

    this.addressControl.valueChanges.pipe(debounceTime(1000), takeUntil(this.destroyed$$)).subscribe(() => {
      this.onChangeAddress();
    });
  }

  onChangeAddress() {
    this.setStatus('input');
    this.formGroup.patchValue({ latitude: 0, longitude: 0 });
    this.validateAddress();
  }

  private validateAddress() {
    if (!this.addressControl.value) {
      this.setStatus('invalid');
      return;
    }

    if (this.latitudeControl.value && this.longitudeControl.value) {
      this.setStatus('valid');
      return;
    }

    this.setStatus('pending');

    this.yaGeocoderService
      .geocode(this.addressControl.value, { results: 1 })
      .pipe(
        takeUntil(this.destroyed$$),
        catchError(err => {
          this.alertService.error(err);
          this.setStatus('invalid');

          return throwError(() => err);
        }),
      )
      // TODO: избавиться от any
      .subscribe((res: any) => {
        const firstGeoObject = res.geoObjects.get(0);

        if (!firstGeoObject?.geometry) {
          this.alertService.error(this.translateService.instant('components.geocoder.labels.hintInvalid'));
          this.setStatus('invalid');
          return;
        }

        const [latitude, longitude] = firstGeoObject.geometry.getCoordinates();
        this.formGroup.patchValue({ latitude, longitude });

        this.setStatus('valid');
      });
  }

  private setStatus(status: ValidationStatusType) {
    this.validationStatus = status;

    if (status === 'invalid') {
      this.addressControl.setErrors({ addressInvalid: true });
    } else if (this.addressControl?.errors && this.addressControl.hasError('contactsInvalid')) {
      delete this.addressControl.errors['addressInvalid'];
    }

    this.cdr.markForCheck();
  }
}
